import type { PageSection } from '../../models/end-client-reporting-package';

/**
 * Drilling into a crosstab: a reader's own exploration of a table, layered on top of the block the
 * author saved. Held by the deck per block and never written to the design -- two people reading the
 * same link explore independently, and closing the deck puts the table back as it was published.
 *
 * Kept as a plain description of what was clicked, so the arithmetic stays where it always is: the
 * deck turns this into an ordinary crosstab block with a longer filter and different questions, and
 * `seriesForBlock` computes it exactly as it would a block the author had saved that way.
 */

/** One drill: an answer the reader narrowed the table to. */
export interface ExploreStep {
  variableKey: string;
  answer: string;
  /** Respondents left after this step, unweighted, as it was when the step was taken. */
  n: number;
}

export interface CrosstabExplore {
  /** Every drill so far, in order. Combined with AND, and with the table's own filter. */
  path: ExploreStep[];
  /** Respondents in the table before the first drill, for the breadcrumb's first step. */
  rootN?: number;
  /** The question down the side, where the reader has changed it. */
  rowKey?: string;
  /** The question across the top, where the reader has changed it; null for the Total alone. */
  columnKey?: string | null;
  /** A third variable the reader has added, or null where they have removed the author's. */
  layerBy?: string | null;
  layerView?: 'nested' | 'tabs' | 'grid';
  /** Every answer of the last drill's question side by side, rather than only the one drilled. */
  siblings?: boolean;
}

type Crosstab = Extract<PageSection, { reportType: 'crosstab' }>;

/** Nothing explored: the table exactly as the author saved it. */
export function isPristine(state: CrosstabExplore | undefined): boolean {
  return !state || (!state.path.length && !state.rowKey && state.columnKey === undefined
    && state.layerBy === undefined && !state.layerView);
}

/**
 * The block the reader is looking at. A question that has been drilled on is swapped out of the
 * table -- a column of "Male" inside a table already filtered to men says nothing -- and where the
 * rows themselves were drilled, the first question of the banner moves down the side, which is how
 * "profile the people who gave this answer" reads.
 */
export function exploredBlock(block: Crosstab, state: CrosstabExplore | undefined): Crosstab {
  if (isPristine(state)) return block;
  const explore = state!;
  const extra = block.reportExtraData;

  const siblingStep = explore.siblings ? explore.path[explore.path.length - 1] : undefined;
  const filtering = siblingStep ? explore.path.slice(0, -1) : explore.path;
  const drilled = new Set(explore.path.map(step => step.variableKey));

  const bannerKeys = [
    ...new Set([...(extra.subgroups ?? []).map(subgroup => subgroup.variableKey), extra.groupBy].filter(Boolean)),
  ];
  const rows = explore.rowKey
    ?? (drilled.has(extra.variableKey)
      ? bannerKeys.find(key => !drilled.has(key)) ?? extra.variableKey
      : extra.variableKey);

  let groupBy = extra.groupBy;
  let subgroups = extra.subgroups;
  if (explore.columnKey === null) {
    groupBy = '';
    subgroups = [];
  } else if (explore.columnKey) {
    groupBy = explore.columnKey;
    subgroups = [];
  } else {
    const keep = (key: string) => !drilled.has(key) && key !== rows;
    subgroups = (extra.subgroups ?? []).filter(subgroup => keep(subgroup.variableKey));
    if (!keep(groupBy)) groupBy = subgroups[0]?.variableKey ?? '';
  }

  const authored = extra.layerBy;
  let layerBy = siblingStep
    ? siblingStep.variableKey
    : explore.layerBy === undefined ? authored : explore.layerBy ?? undefined;
  // A slice by a question already drilled on, or already on an axis, would be one slice of itself.
  if (layerBy && !siblingStep && (drilled.has(layerBy) || layerBy === rows || layerBy === groupBy)) {
    layerBy = undefined;
  }

  return {
    ...block,
    reportExtraData: {
      ...extra,
      variableKey: rows,
      groupBy,
      subgroups,
      // A drill on a question the author already filters on replaces the author's answers for it.
      // Appended, it was ORed with them -- the author's North-or-South plus a drill on North still
      // read as North-or-South, and the table did not narrow under a breadcrumb saying it had.
      tableFilter: [
        ...(extra.tableFilter ?? []).filter(entry =>
          !filtering.some(step => step.variableKey === entry.variableKey)),
        ...filtering.map(step => ({ variableKey: step.variableKey, answer: step.answer })),
      ],
      layerBy,
      layerView: siblingStep ? 'grid' : explore.layerView ?? extra.layerView,
      // Nets and the mean are written against the author's row question; they mean nothing on
      // another.
      ...(rows === extra.variableKey ? {} : { nets: [], showMean: false, meanExcludes: [] }),
    },
  };
}

/** A drill appended, its n recorded for the breadcrumb. */
export function drilledInto(
  state: CrosstabExplore | undefined,
  selection: { variableKey: string; answer: string }[],
  n: number,
  rootN: number,
): CrosstabExplore {
  const path = [...(state?.path ?? [])];
  for (const step of selection) {
    // Drilling a second time on the same question replaces the earlier answer rather than ANDing two
    // answers of one single-select question, which would select nobody.
    const at = path.findIndex(existing => existing.variableKey === step.variableKey);
    if (at >= 0) path.splice(at, 1);
    path.push({ ...step, n });
  }
  // The reader's own axes stay, unless the drill is on one of them -- a column of "Female" inside a
  // table already filtered to women says nothing, so that axis goes back to the author's.
  const drilled = new Set(selection.map(step => step.variableKey));
  const keep = (key: string | null | undefined) => key && !drilled.has(key) ? key : undefined;
  return {
    ...state,
    path,
    rootN: state?.path.length ? state.rootN : rootN,
    siblings: false,
    rowKey: keep(state?.rowKey),
    columnKey: state?.columnKey === null ? null : keep(state?.columnKey),
  };
}

/** Back to the layer after `index` steps -- 0 is the table as published, with any view changes. */
export function backTo(state: CrosstabExplore, index: number): CrosstabExplore {
  return { ...state, path: state.path.slice(0, index), siblings: false };
}

/** One step taken out of the middle, keeping the rest. */
export function withoutStep(state: CrosstabExplore, index: number): CrosstabExplore {
  return { ...state, path: state.path.filter((_, i) => i !== index), siblings: false };
}

/** A question a reader can move onto an axis or slice by. */
export interface ExploreQuestion {
  key: string;
  label: string;
  /** The data position, shown as the code the question is known by. */
  code: string;
  /** A numeric battery can go down the side but cannot split respondents into columns or slices. */
  splits: boolean;
}

/**
 * What a crosstab is handed to explore with. Absent where exploring makes no sense -- a thumbnail,
 * a breakout column, a deck being edited -- and the table then reads exactly as it always has.
 */
export interface ExploreHandle {
  state: CrosstabExplore | undefined;
  questions: ExploreQuestion[];
  change(next: CrosstabExplore | null): void;
  /** Keeps the current view as a table of its own. Only for people who can edit the deck. */
  saveAsTable?: () => void;
}
